import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Backup,
  Refresh,
  Security,
  Update,
  Edit,
  Memory,
  Cached,
  Code,
  Speed,
  KeyboardArrowDown,
  ContentCopy,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  clearSiteCache,
  clearSpecificCache,
} from "../../redux/slices/cacheSlice";
import Interceptor from "../../services/Interceptor";

/**
 * Site overview tab with site information and quick actions
 */
const SiteOverviewTab = ({
  site,
  onBackupClick,
  onShowMessage,
  createLoading,
  onNavigateToSettings,
}) => {
  const dispatch = useDispatch();
  const { clearCacheLoading } = useSelector((state) => state.cache);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [cacheMenuAnchor, setCacheMenuAnchor] = useState(null);
  const [cacheDialogOpen, setCacheDialogOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [latestWpVersion, setLatestWpVersion] = useState(null);

  useEffect(() => {
    Interceptor.get("/sites/wp-versions")
      .then((res) => {
        if (res.data?.data?.latestVersion) {
          setLatestWpVersion(res.data.data.latestVersion);
        }
      })
      .catch(() => {});
  }, []);

  const siteProtocol =
    process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http";

  // Clean version strings by removing all unnecessary whitespace, control characters, and binary data
  const cleanVersion = (version) => {
    if (!version) return "N/A";
    return (
      version
        .toString()
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x1F\x7F-\x9F]/g, "") // Remove all control characters
        .replace(/[\s\n\r\t]+/g, "") // Remove whitespace
        .trim()
    );
  };

  const handleClearCache = async () => {
    if (!site?.id) {
      setSnackbar({
        open: true,
        message: "Site ID not found",
        severity: "error",
      });
      return;
    }

    try {
      const result = await dispatch(clearSiteCache(site.id)).unwrap();

      setSnackbar({
        open: true,
        message: result.message || "Cache cleared successfully!",
        severity: "success",
      });

      // Log the detailed results if available
      if (result.data?.results) {
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to clear cache",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCacheMenuOpen = (event) => {
    setCacheMenuAnchor(event.currentTarget);
  };

  const handleCacheMenuClose = () => {
    setCacheMenuAnchor(null);
  };

  const handleClearAllCaches = async () => {
    handleCacheMenuClose();
    await handleClearCache();
  };

  const handleOpenCacheDialog = () => {
    handleCacheMenuClose();
    setCacheDialogOpen(true);
  };

  const handleClearSpecificCache = async (cacheType) => {
    if (!site?.id) {
      setSnackbar({
        open: true,
        message: "Site ID not found",
        severity: "error",
      });
      return;
    }

    try {
      const result = await dispatch(
        clearSpecificCache({ siteId: site.id, cacheType }),
      ).unwrap();

      setSnackbar({
        open: true,
        message:
          result.message ||
          `${getCacheTypeName(cacheType)} cleared successfully!`,
        severity: "success",
      });

      if (result.data) {
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || `Failed to clear ${cacheType} cache`,
        severity: "error",
      });
    }
  };

  const getCacheTypeName = (type) => {
    const names = {
      object: "Object Cache",
      rewrite: "Rewrite Rules",
      transient: "Transients",
      opcache: "PHP OPcache",
    };
    return names[type] || type;
  };

  const getCacheIcon = (type) => {
    const icons = {
      object: <Memory />,
      rewrite: <Cached />,
      transient: <Code />,
      opcache: <Speed />,
    };
    return icons[type] || <Refresh />;
  };

  const cacheTypes = [
    {
      type: "object",
      name: "Object Cache",
      description: "WordPress object cache",
    },
    {
      type: "rewrite",
      name: "Rewrite Rules",
      description: "Permalink rewrite rules",
    },
    { type: "transient", name: "Transients", description: "Transient data" },
    { type: "opcache", name: "PHP OPcache", description: "PHP opcache" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Site Information
          </Typography>
          <List>
            <ListItem
              sx={{ px: 0 }}
              secondaryAction={
                onNavigateToSettings && (
                  <Tooltip title="Manage in Settings">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={onNavigateToSettings}
                      sx={{ color: "primary.main" }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            >
              <ListItemText
                primary="Domain"
                secondary={
                  <Box component="span">
                    {site?.customDomain || site?.domain}
                    {site?.customDomain && (
                      <Chip
                        component="span"
                        size="small"
                        label={
                          site.metadata?.domainVerified
                            ? "Verified"
                            : "Pending DNS"
                        }
                        color={
                          site.metadata?.domainVerified ? "success" : "warning"
                        }
                        sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                }}
              />
            </ListItem>
            {site?.customDomain && (
              <>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Platform Domain"
                    secondary={site?.domain}
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                    secondaryTypographyProps={{
                      variant: "body1",
                      color: "text.primary",
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                    }}
                  />
                </ListItem>
              </>
            )}
            <Divider />
            <ListItem sx={{ px: 0 }}>
              <ListItemText
                primary="Environment"
                secondary={site?.environment || "N/A"}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  textTransform: "capitalize",
                }}
              />
            </ListItem>
            <Divider />
            <ListItem sx={{ px: 0 }}>
              <ListItemText
                primary="WordPress Version"
                secondary={cleanVersion(
                  site?.versionInfo?.wpVersion || site?.wpVersion,
                )}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                }}
              />
            </ListItem>
            <Divider />
            <ListItem sx={{ px: 0 }}>
              <ListItemText
                primary="PHP Version"
                secondary={cleanVersion(
                  site?.versionInfo?.phpVersion || site?.phpVersion,
                )}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                }}
              />
            </ListItem>
            <Divider />
            <ListItem sx={{ px: 0 }}>
              <ListItemText
                primary="Created Date"
                secondary={
                  site?.createdAt
                    ? new Date(site.createdAt).toLocaleDateString()
                    : "N/A"
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                }}
              />
            </ListItem>
            <Divider />
            <ListItem sx={{ px: 0 }}>
              <ListItemText
                primary="SSL"
                secondary={site?.sslEnabled ? "Enabled" : "Disabled"}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: site?.sslEnabled ? "success.main" : "text.primary",
                }}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Quick Actions
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Update />}
              fullWidth
              color={
                latestWpVersion &&
                site?.versionInfo?.wpVersion &&
                site.versionInfo.wpVersion !== latestWpVersion
                  ? "warning"
                  : "primary"
              }
              onClick={() => {
                const currentVersion =
                  site?.versionInfo?.wpVersion || site?.wpVersion || "unknown";
                if (latestWpVersion && currentVersion !== latestWpVersion) {
                  onShowMessage(
                    `WordPress ${currentVersion} installed. Version ${latestWpVersion} is available. Automatic updates coming in a future release.`,
                    "info",
                  );
                } else {
                  onShowMessage(
                    `WordPress ${currentVersion} is installed and up to date.`,
                    "success",
                  );
                }
              }}
            >
              {latestWpVersion &&
              site?.versionInfo?.wpVersion &&
              site.versionInfo.wpVersion !== latestWpVersion
                ? `Update WordPress (${latestWpVersion} available)`
                : "Update WordPress"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              endIcon={<KeyboardArrowDown />}
              fullWidth
              onClick={handleCacheMenuOpen}
              disabled={clearCacheLoading}
            >
              {clearCacheLoading ? "Clearing Cache..." : "Clear Cache"}
            </Button>
            <Menu
              anchorEl={cacheMenuAnchor}
              open={Boolean(cacheMenuAnchor)}
              onClose={handleCacheMenuClose}
            >
              <MenuItem onClick={handleClearAllCaches}>
                <ListItemIcon>
                  <Refresh fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Clear All Caches" />
              </MenuItem>
              <MenuItem onClick={handleOpenCacheDialog}>
                <ListItemIcon>
                  <Cached fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Clear Specific Cache" />
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              startIcon={<Security />}
              fullWidth
              disabled
              sx={{ opacity: 0.6 }}
            >
              Security Scan (Coming Soon)
            </Button>
            <Button
              variant="outlined"
              startIcon={<Backup />}
              fullWidth
              onClick={onBackupClick}
              disabled={createLoading}
            >
              {createLoading ? "Creating Backup..." : "Quick Backup"}
            </Button>
          </Box>
        </Grid>

        {/* Site Credentials Section — Full-width two-column layout */}
        <Grid size={12}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Site Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click the copy icon to copy credentials to clipboard
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {[
                {
                  label: "Site URL",
                  value: `${siteProtocol}://${site?.customDomain || site?.domain}`,
                },
                {
                  label: "WP Admin URL",
                  value: `${siteProtocol}://${site?.customDomain || site?.domain}/wp-admin`,
                },
                {
                  label: "Admin Username",
                  value: site?.adminUsername || "admin",
                },
                {
                  label: "Admin Password",
                  value: site?.metadata?.adminPassword || "N/A",
                  sensitive: true,
                },
                {
                  label: "Admin Email",
                  value: site?.adminEmail || "N/A",
                },
                {
                  label: "DB Name",
                  value: site?.dbName || "N/A",
                },
                {
                  label: "DB User",
                  value: site?.dbUser || "N/A",
                },
                {
                  label: "DB Password",
                  value: site?.dbPassword || "N/A",
                  sensitive: true,
                },
              ].map((credential, idx) => (
                <Box
                  key={credential.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    px: 2,
                    bgcolor: idx % 2 === 0 ? "transparent" : "action.hover",
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:last-child, &:nth-last-of-type(2)": {
                      borderBottom: { md: 0 },
                    },
                    "&:last-child": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      {credential.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                      }}
                    >
                      {credential.sensitive && !showPasswords[credential.label]
                        ? "••••••••••••"
                        : credential.value}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                    {credential.sensitive && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          setShowPasswords((prev) => ({
                            ...prev,
                            [credential.label]: !prev[credential.label],
                          }))
                        }
                        sx={{ color: "text.secondary" }}
                      >
                        {showPasswords[credential.label] ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(credential.value);
                        setSnackbar({
                          open: true,
                          message: `${credential.label} copied to clipboard!`,
                          severity: "success",
                        });
                      }}
                      sx={{ color: "primary.main" }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Clear Specific Cache Dialog */}
      <Dialog
        open={cacheDialogOpen}
        onClose={() => setCacheDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clear Specific Cache</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select which cache type you want to clear
          </Typography>
          <Stack spacing={2}>
            {cacheTypes.map((cache) => (
              <Box
                key={cache.type}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ color: "primary.main" }}>
                    {getCacheIcon(cache.type)}
                  </Box>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {cache.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cache.description}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleClearSpecificCache(cache.type)}
                  disabled={clearCacheLoading}
                >
                  Clear
                </Button>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCacheDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteOverviewTab;
